import React, { useState, useEffect } from 'react';
import { Box, Typography, Slide } from '@mui/material';
import WifiOffIcon from '@mui/icons-material/WifiOff';

/**
 * OfflineWarningBanner - Shows a warning banner when user goes offline
 * Displays at the top of the page with Freecomers theme styling
 */
const OfflineWarningBanner = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showBanner, setShowBanner] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      // Keep banner visible briefly to show "Back online" state, then hide
      setTimeout(() => {
        setShowBanner(false);
      }, 2000);
    };

    const handleOffline = () => {
      setIsOffline(true);
      setShowBanner(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check
    if (!navigator.onLine) {
      setIsOffline(true);
      setShowBanner(true);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!showBanner) {
    return null;
  }

  return (
    <Slide direction="down" in={showBanner} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          py: 1,
          px: 2,
          bgcolor: isOffline ? 'error.main' : 'success.main',
          color: 'white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          transition: 'background-color 0.3s ease',
        }}
      >
        {isOffline ? (
          <>
            <WifiOffIcon sx={{ fontSize: 20 }} />
            <Typography variant="body2" fontWeight={500}>
              You are offline. Please check your internet connection.
            </Typography>
          </>
        ) : (
          <>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'white',
                animation: 'pulse 1s infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                },
              }}
            />
            <Typography variant="body2" fontWeight={500}>
              Back online!
            </Typography>
          </>
        )}
      </Box>
    </Slide>
  );
};

export default OfflineWarningBanner;
